import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Alert, Modal, Button } from 'react-bootstrap';
import Select from 'react-select';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import CreatableSelect from 'react-select/creatable';
import {
  ChevronLeft, ChevronRight, Plus, Send, Lock, Trash2, Clock, X,
  AlertTriangle, Copy, Umbrella, Thermometer, Baby, Heart,
  CheckCircle2, Table, Calendar as CalendarIcon,
} from 'lucide-react';
import PageTitle from '../../../layouts/PageTitle';
import * as api from '../../../../services/api/timeReportingApi';
import * as payrollApi from '../../../../services/api/payrollApi';
import * as projectsApi from '../../../../services/api/projectsApi';
import * as S from '../../Common/internalToolsStyles';
import MyPeriodsBoard from './MyPeriodsBoard';
import TimerWidget from './TimerWidget';
import WeekCalendarGrid from './WeekCalendarGrid';
import ProjectModal from '../Projects/ProjectModal';
import { startOfWeek, endOfWeek, eachDayOfInterval, addDays, format, isSameDay, isWeekend } from 'date-fns';
import { parseTimeInputHours } from './timeUtils';
import { CATEGORY_LABELS, CATEGORY_OPTIONS } from './timeConstants';

const TARGET_HOURS = 40;
const HISTORY_VIEW_KEY = 'timesheet:historyView';
const GRID_VIEW_KEY = 'timesheet:gridView';
const WEEK_DAYS_KEY = 'timesheet:weekDays';

const ABSENCE_BUTTONS = [
  { code: 'SEMESTER', label: 'Semester',  Icon: Umbrella,    color: '#f59e0b' },
  { code: 'SJUK',     label: 'Sjuk',      Icon: Thermometer, color: '#dc2626' },
  { code: 'VAB',      label: 'VAB',       Icon: Baby,        color: '#7c3aed' },
  { code: 'FP',       label: 'Föräldra',  Icon: Heart,       color: '#ec4899' },
];

const TimesheetPage = () => {
  const { companyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [compBalanceMinutes, setCompBalanceMinutes] = useState(0);
  const [vacationSnapshot, setVacationSnapshot] = useState(null);

  const [weekOffset, setWeekOffset] = useState(0);
  const [extraProjectRows, setExtraProjectRows] = useState([]);
  const [extraLeaveTypeIds, setExtraLeaveTypeIds] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editingRowKey, setEditingRowKey] = useState(null);

  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showCopyPreview, setShowCopyPreview] = useState(false);
  const [copyPreviewEntries, setCopyPreviewEntries] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  // Persisted history view toggle — same pattern as Dokument + Hantera utlägg.
  const [historyView, setHistoryView] = useState(() => {
    try { return localStorage.getItem(HISTORY_VIEW_KEY) || 'kanban'; }
    catch (e) { return 'kanban'; }
  });
  useEffect(() => {
    try { localStorage.setItem(HISTORY_VIEW_KEY, historyView); } catch (e) { /* ignore */ }
  }, [historyView]);
  // Bumped to re-trigger MyPeriodsBoard's data fetch.
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  // Grid view toggle — 'timesheet' (weekly table) or 'calendar' (time-of-day).
  const [gridView, setGridView] = useState(() => {
    try { return localStorage.getItem(GRID_VIEW_KEY) || 'timesheet'; }
    catch (e) { return 'timesheet'; }
  });
  useEffect(() => {
    try { localStorage.setItem(GRID_VIEW_KEY, gridView); } catch (e) { /* ignore */ }
  }, [gridView]);

  // 5-day (Mon-Fri) vs 7-day (Mon-Sun). Purely visual — entries on hidden
  // days still exist in the period and still submit; we just don't render
  // those columns. Applies to both Timesheet and Kalender.
  const [weekDayCount, setWeekDayCount] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(WEEK_DAYS_KEY) || '7', 10);
      return v === 5 ? 5 : 7;
    } catch (e) { return 7; }
  });
  useEffect(() => {
    try { localStorage.setItem(WEEK_DAYS_KEY, String(weekDayCount)); } catch (e) { /* ignore */ }
  }, [weekDayCount]);

  // Quick-create project — triggered from the "+ Lägg till rad…" dropdown
  // when the user types a name that doesn't match any existing project.
  const [createProjectName, setCreateProjectName] = useState(null);

  // Collapsed add-row: shows a centered button by default, expands to the
  // CreatableSelect when clicked. Collapses again after a pick.
  const [showAddRow, setShowAddRow] = useState(false);

  // Absence modal
  const [absenceModal, setAbsenceModal] = useState(null);
  const [absFrom, setAbsFrom] = useState('');
  const [absTo, setAbsTo] = useState('');
  const [absHalfDay, setAbsHalfDay] = useState(false);
  const [absChild, setAbsChild] = useState('');
  const [absNote, setAbsNote] = useState('');

  const today = new Date();
  const baseDate = addDays(today, weekOffset * 7);
  const weekStart = startOfWeek(baseDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(baseDate, { weekStartsOn: 1 });
  const allDays = eachDayOfInterval({ start: weekStart, end: weekEnd });
  const days = weekDayCount === 5 ? allDays.slice(0, 5) : allDays;
  const wsStr = format(weekStart, 'yyyy-MM-dd');
  const weStr = format(weekEnd, 'yyyy-MM-dd');
  const isReadOnly = currentPeriod && ['submitted', 'approved', 'locked'].includes(currentPeriod.status);
  const weekdayNames = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];

  const loadData = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const [projRes, entRes, periodRes, leaveRes, compRes, vacRes] = await Promise.allSettled([
        projectsApi.getMyProjects(companyId),
        api.getTimeEntries(companyId, { startDate: wsStr, endDate: weStr }),
        api.findPeriod(companyId, { periodStart: wsStr, periodEnd: weStr }),
        api.getLeaveTypes(companyId),
        api.getCompBalance(companyId),
        payrollApi.getVacationBalance(companyId),
      ]);
      if (projRes.status === 'fulfilled') {
        setProjects(projRes.value.data.projects || []);
      }
      if (entRes.status === 'fulfilled') setEntries(entRes.value.data.entries || []);
      if (periodRes.status === 'fulfilled') setCurrentPeriod(periodRes.value.data?.period || null);
      if (leaveRes.status === 'fulfilled') setLeaveTypes(leaveRes.value.data.leaveTypes || []);
      if (compRes.status === 'fulfilled') setCompBalanceMinutes(compRes.value.data?.balanceMinutes || 0);
      if (vacRes.status === 'fulfilled') setVacationSnapshot(vacRes.value.data?.snapshot || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, wsStr, weStr]);

  useEffect(() => {
    loadData();
    setExtraProjectRows([]);
    setExtraLeaveTypeIds([]);
    setEditingRowKey(null);
  }, [loadData]);

  const weekEntries = useMemo(
    () => entries.filter(e => e.entry_date >= wsStr && e.entry_date <= weStr),
    [entries, wsStr, weStr],
  );

  const projectMap = useMemo(() => {
    const m = {};
    for (const p of projects) m[p.project_id] = p;
    return m;
  }, [projects]);

  const projectRows = useMemo(() => {
    const seen = new Set();
    const rows = [];
    const add = (projectId, workCategory) => {
      const key = `${projectId}:${workCategory}`;
      if (seen.has(key)) return;
      const project = projectMap[projectId];
      if (!project) return;
      seen.add(key);
      rows.push({ key, projectId, workCategory, project });
    };
    for (const e of weekEntries) {
      if (e.kind === 'work' && e.project_id) add(e.project_id, e.work_category || 'ordinary');
    }
    for (const r of extraProjectRows) add(r.projectId, r.workCategory || 'ordinary');
    return rows;
  }, [weekEntries, extraProjectRows, projectMap]);

  const weekLeaveTypeIds = useMemo(() => {
    const ids = new Set();
    weekEntries.forEach(e => { if (e.kind === 'absence' && e.leave_type_id) ids.add(e.leave_type_id); });
    extraLeaveTypeIds.filter(Boolean).forEach(id => ids.add(id));
    return Array.from(ids);
  }, [weekEntries, extraLeaveTypeIds]);

  const getProjectHours = (pid, category, day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return weekEntries
      .filter(e => e.kind === 'work' && e.project_id === pid && (e.work_category || 'ordinary') === category && e.entry_date === dayStr)
      .reduce((s, e) => s + (e.duration_minutes || 0), 0) / 60;
  };
  const getLeaveHours = (ltId, day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return weekEntries
      .filter(e => e.kind === 'absence' && e.leave_type_id === ltId && e.entry_date === dayStr)
      .reduce((s, e) => s + (e.duration_minutes || 0), 0) / 60;
  };
  const getDayTotal = (day) => weekEntries.filter(e => e.entry_date === format(day, 'yyyy-MM-dd')).reduce((s, e) => s + (e.duration_minutes || 0), 0) / 60;
  const getProjectTotal = (pid, category) => days.reduce((s, d) => s + getProjectHours(pid, category, d), 0);
  const getLeaveTotal = (ltId) => days.reduce((s, d) => s + getLeaveHours(ltId, d), 0);
  const getWeekTotal = () => days.reduce((s, d) => s + getDayTotal(d), 0);
  const getWeekWorked = () => weekEntries.filter(e => e.kind === 'work').reduce((s, e) => s + (e.duration_minutes || 0), 0) / 60;
  const getWeekLeave = () => weekEntries.filter(e => e.kind === 'absence').reduce((s, e) => s + (e.duration_minutes || 0), 0) / 60;
  const fmtH = (h) => h > 0 ? h.toFixed(h % 1 ? 1 : 0) : '';

  const handleCellClick = (id, di, { isLeave = false, category = 'ordinary' } = {}) => {
    if (isReadOnly) return;
    const hours = isLeave ? getLeaveHours(id, days[di]) : getProjectHours(id, category, days[di]);
    setEditingCell(isLeave ? { ltId: id, di } : { pid: id, category, di });
    setEditValue(fmtH(hours));
  };

  const editableRowList = useMemo(() => {
    const list = projectRows.map(r => ({ kind: 'project', pid: r.projectId, category: r.workCategory }));
    for (const ltId of weekLeaveTypeIds) list.push({ kind: 'leave', ltId });
    return list;
  }, [projectRows, weekLeaveTypeIds]);

  const cellRowIndex = (cell) => {
    if (!cell) return -1;
    return editableRowList.findIndex(r =>
      cell.pid
        ? (r.kind === 'project' && r.pid === cell.pid && r.category === (cell.category || 'ordinary'))
        : (r.kind === 'leave' && r.ltId === cell.ltId),
    );
  };

  const cellAt = (rowIndex, di) => {
    if (rowIndex < 0 || rowIndex >= editableRowList.length) return null;
    if (di < 0 || di > 6) return null;
    const r = editableRowList[rowIndex];
    return r.kind === 'project' ? { pid: r.pid, category: r.category, di } : { ltId: r.ltId, di };
  };

  const advanceCell = (cell, dir) => {
    const ri = cellRowIndex(cell);
    if (ri < 0) return null;
    let nextRi = ri;
    let nextDi = cell.di;
    if (dir === 'right') { nextDi += 1; if (nextDi > 6) { nextRi += 1; nextDi = 0; } }
    else if (dir === 'left') { nextDi -= 1; if (nextDi < 0) { nextRi -= 1; nextDi = 6; } }
    else if (dir === 'down') nextRi += 1;
    else if (dir === 'up') nextRi -= 1;
    return cellAt(nextRi, nextDi);
  };

  const projectedDayTotal = (cell, hours) => {
    if (!cell) return 0;
    const dayStr = format(days[cell.di], 'yyyy-MM-dd');
    const dayEntries = weekEntries.filter(e => e.entry_date === dayStr);
    let baseMinutes = 0, alreadyOnRow = 0;
    for (const e of dayEntries) {
      const onThisRow = cell.pid
        ? (e.kind === 'work' && e.project_id === cell.pid && (e.work_category || 'ordinary') === (cell.category || 'ordinary'))
        : (e.kind === 'absence' && e.leave_type_id === cell.ltId);
      if (onThisRow) alreadyOnRow += (e.duration_minutes || 0);
      baseMinutes += (e.duration_minutes || 0);
    }
    const incomingMinutes = (isNaN(hours) || hours <= 0) ? 0 : Math.round(hours * 60);
    return Math.round(((baseMinutes - alreadyOnRow + incomingMinutes) / 60) * 100) / 100;
  };

  // ─── Local entry-state helpers ───
  // Mutation handlers splice the response into `entries` directly so we
  // don't refetch the whole page on every cell save. Keeps the screen
  // from flickering and avoids 6 GETs after a 1-cell change.
  const upsertEntry = (prev, entry) => {
    if (!entry) return prev;
    const idx = prev.findIndex(e => e.entry_id === entry.entry_id);
    if (idx === -1) return [...prev, entry];
    const next = prev.slice();
    next[idx] = entry;
    return next;
  };
  const upsertEntries = (prev, entriesToUpsert) => entriesToUpsert.reduce(upsertEntry, prev);
  const removeEntries = (prev, ids) => {
    const set = new Set(ids);
    return prev.filter(e => !set.has(e.entry_id));
  };

  const handleCellSave = async (advanceTo = null) => {
    if (!editingCell) return;
    const pendingHours = parseTimeInputHours(editValue);
    if (!isNaN(pendingHours) && pendingHours > 0) {
      const projected = projectedDayTotal(editingCell, pendingHours);
      if (projected > 24) {
        setError(`Dygnstotalen blir ${projected}h, max är 24h. Justera eller rensa befintliga rader först.`);
        setEditingCell(null);
        return;
      }
    }
    const h = parseTimeInputHours(editValue);
    const dayStr = format(days[editingCell.di], 'yyyy-MM-dd');

    // Multi-entry cell handling: the grid's cell model is one-entry-per-cell,
    // but timer-driven flows (and a 2026-05-12 TZ bug) can create multiple
    // entries on the same (employee, project, date, category) key. Collect
    // ALL matching entries, not just the first — the previous `.find()`
    // updated one and left siblings contributing their duration to the total,
    // which is what produced the "stuck at 4h" symptom in the user's tenant.
    const matching = weekEntries.filter(e =>
      e.entry_date === dayStr && (
        (editingCell.pid
          && e.kind === 'work'
          && e.project_id === editingCell.pid
          && (e.work_category || 'ordinary') === (editingCell.category || 'ordinary'))
        || (editingCell.ltId && e.kind === 'absence' && e.leave_type_id === editingCell.ltId)
      ),
    );

    try {
      setSaving(true);
      if (matching.length > 0 && (isNaN(h) || h <= 0)) {
        // Clear → delete every matching entry (was deleting only the first).
        for (const e of matching) {
          await api.deleteTimeEntry(companyId, e.entry_id);
        }
        setEntries(prev => removeEntries(prev, matching.map(e => e.entry_id)));
      } else if (matching.length > 0 && h > 0) {
        // Collapse: keep the newest entry (preserves description/interval the
        // user most recently set), update it to the typed value, delete the
        // older siblings. The cell view doesn't show start_time/end_time;
        // intervals on collapsed-away entries are lost, but that's an
        // acceptable trade for the cell-as-source-of-truth contract.
        const sorted = [...matching].sort((a, b) =>
          new Date(b.created_at || 0) - new Date(a.created_at || 0));
        const keeper = sorted[0];
        const toDelete = sorted.slice(1);
        const res = await api.updateTimeEntry(companyId, keeper.entry_id, { durationMinutes: Math.round(h * 60) });
        for (const e of toDelete) {
          await api.deleteTimeEntry(companyId, e.entry_id);
        }
        setEntries(prev => {
          let next = removeEntries(prev, toDelete.map(e => e.entry_id));
          if (res.data?.entry) next = upsertEntry(next, res.data.entry);
          return next;
        });
      } else if (matching.length === 0 && !isNaN(h) && h > 0) {
        const data = { entryDate: dayStr, durationMinutes: Math.round(h * 60) };
        if (editingCell.pid) {
          data.kind = 'work';
          data.projectId = editingCell.pid;
          data.workCategory = editingCell.category || 'ordinary';
        } else if (editingCell.ltId) {
          data.kind = 'absence';
          data.leaveTypeId = editingCell.ltId;
        }
        const res = await api.createTimeEntry(companyId, data);
        if (res.data?.entry) setEntries(prev => upsertEntry(prev, res.data.entry));
        // First entry in a fresh week creates the period server-side; pick
        // it up so the Submit button appears without a navigation refresh.
        if (res.data?.period && !currentPeriod) setCurrentPeriod(res.data.period);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte spara');
    } finally {
      setSaving(false);
      if (advanceTo) {
        const next = advanceCell(editingCell, advanceTo);
        if (next) {
          const hours = next.pid ? getProjectHours(next.pid, next.category, days[next.di]) : getLeaveHours(next.ltId, days[next.di]);
          setEditingCell(next);
          setEditValue(fmtH(hours));
          return;
        }
      }
      setEditingCell(null);
    }
  };

  const handleEditorKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); setEditingCell(null); return; }
    let dir = null;
    if (e.key === 'Enter') dir = 'right';
    else if (e.key === 'Tab' && !e.shiftKey) dir = 'right';
    else if (e.key === 'Tab' && e.shiftKey) dir = 'left';
    else if (e.key === 'ArrowRight') dir = 'right';
    else if (e.key === 'ArrowLeft') dir = 'left';
    else if (e.key === 'ArrowDown') dir = 'down';
    else if (e.key === 'ArrowUp') dir = 'up';
    if (!dir) return;
    e.preventDefault();
    handleCellSave(dir);
  };

  const handleDeleteRow = async (id, { isLeave = false, category = 'ordinary' } = {}) => {
    const rowEntries = weekEntries.filter(e =>
      isLeave
        ? (e.kind === 'absence' && e.leave_type_id === id)
        : (e.kind === 'work' && e.project_id === id && (e.work_category || 'ordinary') === category),
    );
    if (rowEntries.length === 0) {
      if (isLeave) setExtraLeaveTypeIds(prev => prev.filter(ltId => ltId !== id));
      else setExtraProjectRows(prev => prev.filter(r => !(r.projectId === id && r.workCategory === category)));
      return;
    }
    const totalHours = rowEntries.reduce((s, e) => s + (e.duration_minutes || 0), 0) / 60;
    const label = isLeave
      ? leaveTypes.find(t => t.leave_type_id === id)?.name || 'frånvaro'
      : projectMap[id]?.name || 'projekt';
    // eslint-disable-next-line no-restricted-globals, no-alert
    const confirmed = window.confirm(
      `Ta bort raden "${label}"?\n\n${rowEntries.length} post${rowEntries.length === 1 ? '' : 'er'}, totalt ${totalHours}h, försvinner. Det går inte att ångra.`,
    );
    if (!confirmed) return;
    try {
      setSaving(true);
      const removedIds = [];
      for (const entry of rowEntries) {
        await api.deleteTimeEntry(companyId, entry.entry_id);
        removedIds.push(entry.entry_id);
      }
      setEntries(prev => removeEntries(prev, removedIds));
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ta bort');
    } finally { setSaving(false); }
  };

  const handleRowProjectChange = async (oldPid, category, newPid) => {
    const rowEntries = weekEntries.filter(e => e.kind === 'work' && e.project_id === oldPid && (e.work_category || 'ordinary') === category);
    try {
      setSaving(true);
      const updated = [];
      for (const entry of rowEntries) {
        const res = await api.updateTimeEntry(companyId, entry.entry_id, { projectId: newPid });
        if (res.data?.entry) updated.push(res.data.entry);
      }
      setEditingRowKey(null);
      setExtraProjectRows(prev => prev.map(r => (r.projectId === oldPid && r.workCategory === category ? { projectId: newPid, workCategory: category } : r)));
      setEntries(prev => upsertEntries(prev, updated));
    } catch (err) { setError(err.response?.data?.message || 'Kunde inte ändra rad'); }
    finally { setSaving(false); }
  };

  const handleRowCategoryChange = async (pid, oldCategory, newCategory) => {
    if (oldCategory === newCategory) return;
    const rowEntries = weekEntries.filter(e => e.kind === 'work' && e.project_id === pid && (e.work_category || 'ordinary') === oldCategory);
    try {
      setSaving(true);
      const updated = [];
      for (const entry of rowEntries) {
        const res = await api.updateTimeEntry(companyId, entry.entry_id, { workCategory: newCategory });
        if (res.data?.entry) updated.push(res.data.entry);
      }
      setExtraProjectRows(prev => prev.map(r => (r.projectId === pid && r.workCategory === oldCategory ? { projectId: pid, workCategory: newCategory } : r)));
      setEntries(prev => upsertEntries(prev, updated));
    } catch (err) { setError(err.response?.data?.message || 'Kunde inte ändra typ'); }
    finally { setSaving(false); }
  };

  const handleSubmitConfirm = async () => {
    setShowSubmitConfirm(false);
    if (!currentPeriod) return;
    try {
      setSaving(true);
      const res = await api.submitPeriod(companyId, currentPeriod.period_id);
      setSubmitSuccess(res.data?.period?.autoApproved || res.data?.autoApproved ? 'autoApproved' : 'submitted');
      await loadData();
    } catch (err) { setError(err.response?.data?.message || 'Kunde inte skicka in'); }
    finally { setSaving(false); }
  };

  const handleWithdraw = async () => {
    if (!currentPeriod) return;
    try {
      setSaving(true);
      await api.withdrawPeriod(companyId, currentPeriod.period_id);
      await loadData();
    } catch (err) { setError(err.response?.data?.message || 'Kunde inte dra tillbaka'); }
    finally { setSaving(false); }
  };

  const handleCopyPreviousWeekClick = async () => {
    try {
      setSaving(true);
      const prevStart = format(addDays(weekStart, -7), 'yyyy-MM-dd');
      const prevEnd = format(addDays(weekEnd, -7), 'yyyy-MM-dd');
      const prevRes = await api.getTimeEntries(companyId, { startDate: prevStart, endDate: prevEnd });
      const prevEntries = (prevRes.data.entries || []).filter(e => (
        (e.kind === 'work' && e.project_id) ||
        (e.kind === 'absence' && e.leave_type_id)
      ));
      if (prevEntries.length === 0) {
        setError('Inga poster att kopiera från förra veckan. Logga tid eller frånvaro manuellt först.');
        return;
      }
      setCopyPreviewEntries(prevEntries);
      setShowCopyPreview(true);
    } catch (err) { setError(err.response?.data?.message || 'Kunde inte ladda förra veckan'); }
    finally { setSaving(false); }
  };

  const handleCopyConfirm = async () => {
    setShowCopyPreview(false);
    try {
      setSaving(true);
      const copyCount = copyPreviewEntries.length;
      const created = [];
      let firstPeriod = null;
      for (const e of copyPreviewEntries) {
        const dayOffset = (new Date(e.entry_date).getDay() + 6) % 7;
        const newDate = format(addDays(weekStart, dayOffset), 'yyyy-MM-dd');
        const payload = e.kind === 'absence'
          ? {
              entryDate: newDate,
              durationMinutes: e.duration_minutes,
              kind: 'absence',
              leaveTypeId: e.leave_type_id,
              description: e.description || null,
            }
          : {
              entryDate: newDate,
              durationMinutes: e.duration_minutes,
              kind: 'work',
              projectId: e.project_id,
              workCategory: e.work_category || 'ordinary',
              description: e.description || null,
            };
        const res = await api.createTimeEntry(companyId, payload);
        if (res.data?.entry) created.push(res.data.entry);
        if (!firstPeriod && res.data?.period) firstPeriod = res.data.period;
      }
      setEntries(prev => upsertEntries(prev, created));
      if (firstPeriod && !currentPeriod) setCurrentPeriod(firstPeriod);
      setSubmitSuccess(`copied:${copyCount}`);
    } catch (err) { setError(err.response?.data?.message || 'Kunde inte kopiera'); }
    finally { setSaving(false); setCopyPreviewEntries([]); }
  };

  // Called from ProjectModal when the user confirms a quick-create started
  // from the add-row dropdown. Creates the project, refreshes the project
  // list, and auto-adds it as a row so the user sees immediate progress.
  const handleQuickCreateProject = async (data) => {
    const res = await projectsApi.createProject(companyId, data);
    const newProjectId = res?.data?.projectId || res?.data?.project_id;
    // Refresh so projectMap has the new project — row rendering depends on it.
    try {
      const projRes = await projectsApi.getMyProjects(companyId);
      setProjects(projRes.data.projects || []);
    } catch (e) { /* non-fatal — next loadData will pick it up */ }
    if (newProjectId) {
      setExtraProjectRows(prev => [...prev, { projectId: newProjectId, workCategory: 'ordinary' }]);
    }
    setCreateProjectName(null);
  };

  // ─── Absence quick-add (from old Översikt) ───

  const openAbsenceModal = (btn) => {
    const todayStr = format(today, 'yyyy-MM-dd');
    setAbsFrom(todayStr);
    setAbsTo(todayStr);
    setAbsHalfDay(false);
    setAbsChild('');
    setAbsNote('');
    setAbsenceModal(btn);
  };
  const closeAbsenceModal = () => setAbsenceModal(null);

  const confirmAbsence = async () => {
    if (!absenceModal) return;
    const lt = leaveTypes.find(t => t.code === absenceModal.code);
    if (!lt) { setError(`Frånvarotyp ${absenceModal.code} saknas i systemet`); return; }
    if (!absFrom || !absTo || absTo < absFrom) { setError('Ogiltigt datumintervall'); return; }
    if (absenceModal.code === 'VAB' && !absChild.trim()) {
      setError('Namn på barnet krävs för VAB (Försäkringskassan).');
      return;
    }
    const start = new Date(absFrom + 'T00:00:00');
    const end = new Date(absTo + 'T00:00:00');
    const dayList = eachDayOfInterval({ start, end });
    const durationMinutes = absHalfDay ? 240 : 480;
    const parts = [];
    if (absenceModal.code === 'VAB') parts.push(`Barn: ${absChild.trim()}`);
    if (absNote.trim()) parts.push(absNote.trim());
    const description = parts.length ? parts.join(' — ') : null;

    setSaving(true);
    let successCount = 0;
    let firstError = null;
    let firstWarning = null;
    const created = [];
    let firstPeriod = null;
    try {
      for (const day of dayList) {
        try {
          const res = await api.createTimeEntry(companyId, {
            entryDate: format(day, 'yyyy-MM-dd'),
            durationMinutes,
            kind: 'absence',
            leaveTypeId: lt.leave_type_id,
            description,
          });
          successCount += 1;
          if (res?.data?.entry) created.push(res.data.entry);
          if (!firstPeriod && res?.data?.period) firstPeriod = res.data.period;
          if (!firstWarning && res?.data?.warning?.kind === 'vacation_balance_advance') {
            firstWarning = res.data.warning;
          }
        } catch (err) {
          if (!firstError) firstError = err;
        }
      }
      if (created.length > 0) setEntries(prev => upsertEntries(prev, created));
      if (firstPeriod && !currentPeriod) setCurrentPeriod(firstPeriod);
      if (successCount > 0) {
        const baseMsg = `${lt.name}: ${successCount} dag${successCount === 1 ? '' : 'ar'} registrerad${successCount === 1 ? '' : 'e'}`;
        setSuccess(firstWarning ? `${baseMsg}. ${firstWarning.message}` : baseMsg);
        setTimeout(() => setSuccess(null), firstWarning ? 6000 : 3000);
      }
      if (firstError) {
        setError(firstError.response?.data?.message || `Kunde inte logga alla dagar för ${lt.name}`);
      }
      closeAbsenceModal();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const periodStatus = currentPeriod?.status;
  const canSubmit = (periodStatus === 'open' || periodStatus === 'rejected') && weekEntries.length > 0;
  const canWithdraw = periodStatus === 'submitted';

  return (
    <>
      <PageTitle activeMenu="Min tid" motherMenu="Tid" />
      <style>{timesheetCss}</style>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          <CheckCircle2 size={14} style={{ marginRight: 6 }} /> {success}
        </Alert>
      )}
      {submitSuccess === 'autoApproved' && (
        <Alert variant="success" dismissible onClose={() => setSubmitSuccess(null)}>
          <strong>Automatiskt godkänd!</strong> Vecka {format(weekStart, 'w')} har skickats in och godkänts automatiskt.
        </Alert>
      )}
      {submitSuccess === 'submitted' && (
        <Alert variant="info" dismissible onClose={() => setSubmitSuccess(null)}>
          <strong>Inskickad!</strong> Vecka {format(weekStart, 'w')} väntar nu på godkännande.
        </Alert>
      )}
      {typeof submitSuccess === 'string' && submitSuccess.startsWith('copied:') && (
        <Alert variant="success" dismissible onClose={() => setSubmitSuccess(null)}>
          <strong>{submitSuccess.split(':')[1]} poster kopierade.</strong> Granska och justera innan du skickar in.
        </Alert>
      )}

      {/* Rejection banner */}
      {periodStatus === 'rejected' && currentPeriod?.rejection_reason && (
        <div className="tr-reject-banner">
          <AlertTriangle size={18} style={{ color: S.colors.danger, flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: S.colors.danger, marginBottom: 2 }}>
              Vecka {format(weekStart, 'w')} avvisad
            </div>
            <div style={{ fontSize: 13, color: S.colors.textSecondary, lineHeight: 1.4 }}>
              {currentPeriod.rejection_reason}
            </div>
          </div>
        </div>
      )}

      {/* ─── Page toolbar: week nav · balance pills · actions ─── */}
      <div className="tr-toolbar">
        <div className="tr-toolbar-left">
          <button onClick={() => setWeekOffset(o => o - 1)} style={S.btnIcon} title="Föregående vecka"><ChevronLeft size={16} /></button>
          <button onClick={() => setWeekOffset(0)} style={S.btnSecondary}>Denna vecka</button>
          <button onClick={() => setWeekOffset(o => o + 1)} style={S.btnIcon} title="Nästa vecka"><ChevronRight size={16} /></button>
          <span className="tr-week-label">
            <span className="tr-week-num">v{format(weekStart, 'w')}</span>
            <span className="tr-week-range">{format(weekStart, 'd MMM')} – {format(weekEnd, 'd MMM yyyy')}</span>
          </span>
        </div>
        <div className="tr-toolbar-pills">
          {periodStatus && periodStatus !== 'open' && (
            <span style={S.statusBadge[periodStatus]}>
              {periodStatus === 'locked' && <Lock size={10} />}
              {S.statusLabel[periodStatus] || periodStatus}
            </span>
          )}
          {compBalanceMinutes !== 0 && (
            <span title="Komptidssaldo" className={`tr-pill ${compBalanceMinutes > 0 ? 'tr-pill-comp-pos' : 'tr-pill-comp-neg'}`}>
              Komp: {(compBalanceMinutes / 60).toFixed(compBalanceMinutes % 60 ? 1 : 0)}h
            </span>
          )}
          {vacationSnapshot && (
            <span
              title={`Semesterår ${vacationSnapshot.year}/${vacationSnapshot.year + 1} · Tjänat ${vacationSnapshot.earnedDays}d · Sparat ${vacationSnapshot.savedDays}d${vacationSnapshot.advanceDaysTaken > 0 ? ` · Förskott ${vacationSnapshot.advanceDaysTaken}d` : ''}`}
              className={`tr-pill ${vacationSnapshot.advanceDaysTaken > 0 ? 'tr-pill-vac-advance' : 'tr-pill-vac'}`}
            >
              Semester: {vacationSnapshot.remainingPaidDays}d kvar
            </span>
          )}
        </div>
        <div className="tr-toolbar-right">
          {periodStatus === 'open' && (
            <button onClick={handleCopyPreviousWeekClick} disabled={saving} style={S.btnSecondary} title="Kopiera förra veckans tider">
              <Copy size={14} /> Kopiera förra veckan
            </button>
          )}
          {canWithdraw && (
            <button onClick={handleWithdraw} disabled={saving} style={S.btnSecondary}>
              <X size={14} /> Dra tillbaka
            </button>
          )}
          {canSubmit && (
            <button onClick={() => setShowSubmitConfirm(true)} disabled={saving} style={S.btnPrimary}>
              <Send size={14} /> {periodStatus === 'rejected' ? 'Skicka igen' : 'Skicka'}
            </button>
          )}
        </div>
      </div>

      {/* ─── Min tid heading + quick-action strip ─── */}
      <div className="tr-section-heading" style={{ marginTop: 4 }}>
        <div className="tr-section-title">Min tid</div>
        <div className="tr-section-sub">Rapportera dina timmar och frånvaro</div>
      </div>

      {!isReadOnly && (
        <div className="tr-quick-strip">
          <div className="tr-quick-strip-timer">
            <TimerWidget onTimerChange={loadData} todayMinutes={0} />
          </div>
          <div className="tr-quick-strip-abs">
            {ABSENCE_BUTTONS.map((btn) => {
              const { code, label, Icon, color } = btn;
              return (
                <button
                  key={code}
                  onClick={() => openAbsenceModal(btn)}
                  disabled={saving}
                  className="tr-abs-btn"
                  style={{ '--abs-color': color }}
                >
                  <Icon size={14} /> {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Week grid: toggle between timesheet and calendar ─── */}
      <div className="tr-grid-viewbar">
        <div className="tr-view-switch" role="tablist" aria-label="Antal dagar">
          <button
            role="tab"
            aria-selected={weekDayCount === 5}
            className={`tr-view-btn ${weekDayCount === 5 ? 'active' : ''}`}
            onClick={() => setWeekDayCount(5)}
            title="Mån–Fre"
            aria-label="5-dagarsvecka (Mån–Fre)"
          >
            5d
          </button>
          <button
            role="tab"
            aria-selected={weekDayCount === 7}
            className={`tr-view-btn ${weekDayCount === 7 ? 'active' : ''}`}
            onClick={() => setWeekDayCount(7)}
            title="Mån–Sön"
            aria-label="7-dagarsvecka (Mån–Sön)"
          >
            7d
          </button>
        </div>
        <div className="tr-view-switch" role="tablist" aria-label="Vy för denna vecka">
          <button
            role="tab"
            aria-selected={gridView === 'timesheet'}
            className={`tr-view-btn ${gridView === 'timesheet' ? 'active' : ''}`}
            onClick={() => setGridView('timesheet')}
            title="Timesheet"
            aria-label="Timesheet"
          >
            <Table size={14} /> Timesheet
          </button>
          <button
            role="tab"
            aria-selected={gridView === 'calendar'}
            className={`tr-view-btn ${gridView === 'calendar' ? 'active' : ''}`}
            onClick={() => setGridView('calendar')}
            title="Kalender"
            aria-label="Kalender"
          >
            <CalendarIcon size={14} /> Kalender
          </button>
        </div>
      </div>

      {gridView === 'calendar' ? (
        <div style={S.card}>
          <WeekCalendarGrid
            days={days}
            today={today}
            weekEntries={weekEntries}
            projects={projects}
            leaveTypes={leaveTypes}
            isReadOnly={isReadOnly}
            onCreateEntry={async (payload) => {
              try {
                const res = await api.createTimeEntry(companyId, payload);
                if (res.data?.entry) setEntries(prev => upsertEntry(prev, res.data.entry));
                if (res.data?.period && !currentPeriod) setCurrentPeriod(res.data.period);
              } catch (err) { setError(err.response?.data?.message || 'Kunde inte skapa post'); throw err; }
            }}
            onUpdateEntry={async (entryId, patch) => {
              try {
                const res = await api.updateTimeEntry(companyId, entryId, patch);
                if (res.data?.entry) setEntries(prev => upsertEntry(prev, res.data.entry));
              } catch (err) { setError(err.response?.data?.message || 'Kunde inte uppdatera'); throw err; }
            }}
            onDeleteEntry={async (entryId) => {
              try {
                await api.deleteTimeEntry(companyId, entryId);
                setEntries(prev => removeEntries(prev, [entryId]));
              } catch (err) { setError(err.response?.data?.message || 'Kunde inte ta bort'); throw err; }
            }}
          />
        </div>
      ) : (
      <div style={S.card}>
        <div className="tr-grid-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ ...S.table, minWidth: 760 }}>
            <thead>
              <tr style={S.tableHeaderRow}>
                <th style={{ ...S.tableHeaderCell, width: 240, minWidth: 180, position: 'sticky', left: 0, zIndex: 2, background: S.colors.bgSubtle }}>PROJEKT</th>
                <th style={{ ...S.tableHeaderCellCenter, width: 80 }}>TYP</th>
                {days.map((day, i) => {
                  const isToday = isSameDay(day, today);
                  const isOff = isWeekend(day);
                  const color = isToday ? 'var(--primary)' : isOff ? S.colors.textDisabled : S.colors.textSecondary;
                  return (
                    <th key={i} style={{
                      ...S.tableHeaderCellCenter, width: 66,
                      ...(isToday ? { background: 'var(--rgba-primary-1)' } : isOff ? { background: 'rgba(239,68,68,0.04)' } : {}),
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, color }}>
                          <span style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.04 }}>
                            {weekdayNames[i]}
                          </span>
                          <span style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                            {format(day, 'd')}
                          </span>
                        </div>
                        {/* Small primary-green dot under today's date — the sole
                            navigation anchor for "today" now that the per-cell
                            column tint is removed. 4px keeps it subtle; the
                            header's existing primary-green text + background
                            tint pair with it without crowding. */}
                        {isToday && (
                          <span
                            aria-hidden="true"
                            style={{
                              width: 4, height: 4, borderRadius: '50%',
                              background: 'var(--primary)',
                              marginTop: 1,
                            }}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
                <th style={{ ...S.tableHeaderCellCenter, width: 70 }}>TOTALT</th>
                <th style={{ ...S.tableHeaderCell, width: 44 }}></th>
              </tr>
            </thead>
            <tbody>
              {projectRows.length === 0 && weekLeaveTypeIds.length === 0 && (
                <tr>
                  <td colSpan={days.length + 4} style={{ textAlign: 'center', padding: '48px 20px' }}>
                    <Clock size={36} style={{ color: S.colors.textDisabled, marginBottom: 12 }} />
                    <div style={{ fontSize: 14, color: S.colors.textLight, marginBottom: 4 }}>Inga timmar rapporterade denna vecka</div>
                    <div style={{ fontSize: 12, color: S.colors.textDisabled }}>Använd snabb-loggen ovan eller dropdown:en nedanför tabellen</div>
                  </td>
                </tr>
              )}
              {projectRows.map(row => {
                const { project, projectId: pid, workCategory } = row;
                const total = getProjectTotal(pid, workCategory);
                const isEditingProject = editingRowKey === row.key && !isReadOnly;
                const isEditingCategory = editingRowKey === `${row.key}:type` && !isReadOnly;
                const rowProjectOptions = projects.map(p => ({
                  value: p.project_id,
                  label: `${p.name}${p.code ? ` (${p.code})` : ''}`,
                }));
                const catMeta = CATEGORY_LABELS[workCategory] || CATEGORY_LABELS.ordinary;

                return (
                  <tr
                    key={row.key}
                    style={S.tableRow}
                    onMouseEnter={e => (e.currentTarget.style.background = S.colors.bgSubtle)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ ...S.tableCell, padding: '6px 8px 6px 12px', borderLeft: `3px solid ${project.color || '#6b7280'}` }}>
                      {isEditingProject ? (
                        <Select
                          value={rowProjectOptions.find(o => o.value === pid) || null}
                          options={rowProjectOptions}
                          onChange={opt => {
                            if (!opt || opt.value === pid) { setEditingRowKey(null); return; }
                            handleRowProjectChange(pid, workCategory, opt.value);
                          }}
                          isSearchable
                          autoFocus
                          placeholder="Välj projekt..."
                          menuPortalTarget={document.body}
                          theme={brandSelectTheme}
                          styles={{ ...S.selectStylesCompact, menuPortal: b => ({ ...b, zIndex: 9999 }) }}
                        />
                      ) : (
                        <div
                          onClick={() => !isReadOnly && setEditingRowKey(row.key)}
                          style={{ cursor: isReadOnly ? 'default' : 'pointer', minWidth: 0 }}
                          title={isReadOnly ? undefined : 'Klicka för att byta projekt'}
                        >
                          <div style={{ fontWeight: 600, fontSize: 13, color: S.colors.textPrimary, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {project.name}
                          </div>
                          {project.code && <div style={{ fontSize: 11, color: S.colors.textLight, lineHeight: 1.2 }}>{project.code}</div>}
                        </div>
                      )}
                    </td>
                    <td style={{ ...S.tableCellCenter, padding: '6px 4px' }}>
                      {isEditingCategory ? (
                        <Select
                          value={CATEGORY_OPTIONS.find(o => o.value === workCategory) || CATEGORY_OPTIONS[0]}
                          options={CATEGORY_OPTIONS}
                          onChange={opt => {
                            if (opt) handleRowCategoryChange(pid, workCategory, opt.value);
                            setEditingRowKey(null);
                          }}
                          onBlur={() => setEditingRowKey(null)}
                          isSearchable={false}
                          autoFocus
                          menuPortalTarget={document.body}
                          theme={brandSelectTheme}
                          styles={{ ...S.selectStylesCompact, menuPortal: b => ({ ...b, zIndex: 9999 }) }}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => !isReadOnly && setEditingRowKey(`${row.key}:type`)}
                          disabled={isReadOnly}
                          className="tr-type-pill"
                          style={{
                            background: `${catMeta.color}1a`,
                            color: catMeta.color,
                            borderColor: `${catMeta.color}33`,
                            cursor: isReadOnly ? 'default' : 'pointer',
                          }}
                          title={isReadOnly ? catMeta.label : `${catMeta.label} — klicka för att byta`}
                        >
                          {catMeta.short}
                        </button>
                      )}
                    </td>
                    {days.map((day, di) => {
                      const hours = getProjectHours(pid, workCategory, day);
                      const isEditing = editingCell?.pid === pid && editingCell?.category === workCategory && editingCell?.di === di;
                      return (
                        <td key={di} style={{ ...S.tableCellCenter, padding: '6px 3px', background: isWeekend(day) ? 'rgba(239,68,68,0.04)' : 'transparent' }}>
                          {isEditing ? (
                            // No placeholder: the cell input is center-aligned,
                            // and an empty value's blinking cursor sat right on
                            // top of the "0" placeholder ghost. Click context
                            // (cell, weekday header, row label) already tells
                            // the user what to type.
                            // onFocus selects the existing value so typing
                            // REPLACES rather than appends — matches Toggl /
                            // Bokio / Excel cell-edit convention. Without
                            // this, clicking a "2" cell and typing "3" gave
                            // "23" saved as 23h instead of replacing with 3h.
                            <input
                              type="text"
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onBlur={() => handleCellSave()}
                              onKeyDown={handleEditorKeyDown}
                              onFocus={e => e.target.select()}
                              style={S.timesheetCellEditing}
                              autoFocus
                            />
                          ) : (
                            <button
                              onClick={() => handleCellClick(pid, di, { isLeave: false, category: workCategory })}
                              disabled={isReadOnly}
                              aria-label={`${row.project?.name || 'Projekt'}, ${weekdayNames[di]} ${format(day, 'd MMM')}: ${hours > 0 ? `${hours}h` : 'inga timmar'}`}
                              style={isReadOnly ? S.timesheetCellReadonly : hours > 0 ? S.timesheetCellFilled : S.timesheetCell}
                            >
                              {fmtH(hours) || (isReadOnly ? '' : '+')}
                            </button>
                          )}
                        </td>
                      );
                    })}
                    <td style={{ ...S.tableCellCenter, fontWeight: 700, fontSize: 13, color: total > 0 ? S.colors.textPrimary : S.colors.textDisabled }}>
                      {total > 0 ? `${total.toFixed(total % 1 ? 1 : 0)}h` : '–'}
                    </td>
                    <td style={{ ...S.tableCell, padding: '8px 4px' }}>
                      {!isReadOnly && (
                        <button
                          onClick={() => handleDeleteRow(pid, { isLeave: false, category: workCategory })}
                          style={{ ...S.btnGhost, padding: 4, color: S.colors.textDisabled }}
                          title="Ta bort rad"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}

              {weekLeaveTypeIds.map(ltId => {
                const lt = leaveTypes.find(t => t.leave_type_id === ltId);
                if (!lt) return null;
                const total = getLeaveTotal(ltId);
                const leaveIcons = { SEMESTER: '🏖️', SJUK: '🏥', FP: '👶', VAB: '🧒', KOMP: '⏰', TJL: '📋', PERM: '📋', ARB: '🩹' };
                const icon = leaveIcons[lt.code] || '📋';

                return (
                  <tr
                    key={`leave-${ltId}`}
                    style={S.tableRow}
                    onMouseEnter={e => (e.currentTarget.style.background = S.colors.bgSubtle)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ ...S.tableCell, padding: '6px 12px', borderLeft: '3px solid #f59e0b' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14 }}>{icon}</span>
                        <div style={{ fontWeight: 600, fontSize: 13, color: S.colors.textPrimary }}>{lt.name}</div>
                      </div>
                    </td>
                    <td style={{ ...S.tableCellCenter, padding: '6px 4px' }}>
                      <span className="tr-type-pill tr-type-pill-leave" title="Frånvaro">
                        Frånv
                      </span>
                    </td>
                    {days.map((day, di) => {
                      const hours = getLeaveHours(ltId, day);
                      const isEditing = editingCell?.ltId === ltId && editingCell?.di === di;
                      return (
                        <td key={di} style={{ ...S.tableCellCenter, padding: '6px 3px', background: isWeekend(day) ? 'rgba(239,68,68,0.04)' : 'transparent' }}>
                          {isEditing ? (
                            // No placeholder: the cell input is center-aligned,
                            // and an empty value's blinking cursor sat right on
                            // top of the "0" placeholder ghost. Click context
                            // (cell, weekday header, row label) already tells
                            // the user what to type.
                            // onFocus selects the existing value so typing
                            // REPLACES rather than appends — matches Toggl /
                            // Bokio / Excel cell-edit convention. Without
                            // this, clicking a "2" cell and typing "3" gave
                            // "23" saved as 23h instead of replacing with 3h.
                            <input
                              type="text"
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onBlur={() => handleCellSave()}
                              onKeyDown={handleEditorKeyDown}
                              onFocus={e => e.target.select()}
                              style={S.timesheetCellEditing}
                              autoFocus
                            />
                          ) : (
                            <button
                              onClick={() => handleCellClick(ltId, di, { isLeave: true })}
                              disabled={isReadOnly}
                              style={isReadOnly ? S.timesheetCellReadonly : hours > 0 ? S.timesheetCellFilled : S.timesheetCell}
                            >
                              {fmtH(hours) || (isReadOnly ? '' : '+')}
                            </button>
                          )}
                        </td>
                      );
                    })}
                    <td style={{ ...S.tableCellCenter, fontWeight: 700, fontSize: 13, color: total > 0 ? S.colors.textPrimary : S.colors.textDisabled }}>
                      {total > 0 ? `${total.toFixed(total % 1 ? 1 : 0)}h` : '–'}
                    </td>
                    <td style={{ ...S.tableCell, padding: '8px 4px' }}>
                      {!isReadOnly && (
                        <button
                          onClick={() => handleDeleteRow(ltId, { isLeave: true })}
                          style={{ ...S.btnGhost, padding: 4, color: S.colors.textDisabled }}
                          title="Ta bort rad"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}

              {!isReadOnly && (() => {
                const projectOpts = projects
                  .map(p => {
                    const usedCategories = new Set(
                      projectRows.filter(r => r.projectId === p.project_id).map(r => r.workCategory),
                    );
                    const firstFree = CATEGORY_OPTIONS.find(c => !usedCategories.has(c.value));
                    return firstFree ? {
                      value: `proj:${p.project_id}:${firstFree.value}`,
                      label: `${p.name}${p.code ? ` (${p.code})` : ''}`,
                      kind: 'project',
                      projectId: p.project_id,
                      workCategory: firstFree.value,
                    } : null;
                  })
                  .filter(Boolean);
                const leaveOpts = leaveTypes
                  .filter(lt => !weekLeaveTypeIds.includes(lt.leave_type_id))
                  .map(lt => ({
                    value: `leave:${lt.leave_type_id}`,
                    label: lt.name,
                    kind: 'leave',
                    leaveTypeId: lt.leave_type_id,
                  }));
                const groupedOptions = [];
                if (projectOpts.length) groupedOptions.push({ label: 'Projekt', options: projectOpts });
                if (leaveOpts.length) groupedOptions.push({ label: 'Frånvaro', options: leaveOpts });
                const hasRows = projectRows.length > 0 || weekLeaveTypeIds.length > 0;
                const picker = (
                  <CreatableSelect
                    autoFocus
                    defaultMenuIsOpen
                    options={groupedOptions}
                    onChange={opt => {
                      if (!opt) return;
                      if (opt.kind === 'project') {
                        setExtraProjectRows(prev => [...prev, { projectId: opt.projectId, workCategory: opt.workCategory }]);
                      } else if (opt.kind === 'leave') {
                        setExtraLeaveTypeIds(prev => [...prev, opt.leaveTypeId]);
                      }
                      setShowAddRow(false);
                    }}
                    onBlur={() => setShowAddRow(false)}
                    onCreateOption={(input) => { setCreateProjectName(input.trim()); setShowAddRow(false); }}
                    formatCreateLabel={(input) => `+ Skapa nytt projekt "${input}"`}
                    isValidNewOption={(input, _selectValue, selectOptions) => {
                      const s = input.trim();
                      if (s.length < 2) return false;
                      const allOpts = selectOptions.flatMap(g => g.options || []);
                      return !allOpts.some(o => o.label.toLowerCase() === s.toLowerCase());
                    }}
                    value={null}
                    isSearchable
                    isClearable
                    placeholder="Sök projekt eller frånvaro…"
                    noOptionsMessage={() => 'Inget matchar'}
                    menuPortalTarget={document.body}
                    theme={brandSelectTheme}
                    styles={{
                      ...S.selectStylesCompact,
                      container: b => ({ ...b, width: '100%' }),
                      menuPortal: b => ({ ...b, zIndex: 9999 }),
                      placeholder: b => ({ ...b, fontSize: 13, color: S.colors.textLight }),
                    }}
                  />
                );
                const triggerButton = (
                  <button
                    type="button"
                    onClick={() => setShowAddRow(true)}
                    className="tr-addrow-btn"
                  >
                    <Plus size={14} /> Lägg till rad
                  </button>
                );

                // Two shapes:
                // - Empty grid: center the control in a single full-width cell.
                // - With rows: put it in the PROJEKT+TYP column area (colSpan=2)
                //   so it aligns with where rows start, rest stays empty.
                if (!hasRows) {
                  return (
                    <tr>
                      <td colSpan={days.length + 4} style={{ padding: '12px 16px', borderBottom: 'none' }}>
                        <div className="tr-addrow-inner tr-addrow-centered">
                          {showAddRow ? (<div className="tr-addrow-picker">{picker}</div>) : triggerButton}
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr>
                    <td style={{ padding: '8px 12px', borderBottom: 'none' }}>
                      <div className="tr-addrow-inner">
                        {showAddRow ? (<div className="tr-addrow-picker">{picker}</div>) : triggerButton}
                      </div>
                    </td>
                    <td style={{ borderBottom: 'none' }} />
                    {days.map((day, di) => {
                      const isOff = isWeekend(day);
                      return (
                        <td key={di} style={{
                          borderBottom: 'none',
                          background: isOff ? 'rgba(239,68,68,0.04)' : undefined,
                        }} />
                      );
                    })}
                    <td style={{ borderBottom: 'none' }} />
                    <td style={{ borderBottom: 'none' }} />
                  </tr>
                );
              })()}

              {(projectRows.length > 0 || weekLeaveTypeIds.length > 0) && (
              <tr style={{ borderTop: `2px solid ${S.colors.borderDefault}`, background: S.colors.bgSubtle }}>
                <td colSpan={2} style={{ ...S.tableCell, fontWeight: 600, color: S.colors.textSecondary }}>
                  <div>Dagstotal</div>
                  {getWeekLeave() > 0 && (
                    <div style={{ fontSize: 10, fontWeight: 400, color: S.colors.textLight, marginTop: 2 }}>
                      {fmtH(getWeekWorked())}h arbete · {fmtH(getWeekLeave())}h frånvaro
                    </div>
                  )}
                </td>
                {days.map((day, i) => {
                  const total = getDayTotal(day);
                  const isToday = isSameDay(day, today);
                  const isOff = isWeekend(day);
                  return (
                    <td key={i} style={{
                      ...S.tableCellCenter, fontWeight: 600, fontSize: 13,
                      // Today's day-total text stays primary-green (typography
                      // signal — pairs with the header dot as the two
                      // navigation anchors for "today"). Background tint
                      // dropped along with the per-row column stripe.
                      color: total > 8 ? S.colors.danger : isToday ? 'var(--primary)' : isOff ? S.colors.textDisabled : S.colors.textSecondary,
                      background: isOff ? 'rgba(239,68,68,0.04)' : undefined,
                    }}>
                      {total > 0 ? total.toFixed(total % 1 ? 1 : 0) : '–'}
                    </td>
                  );
                })}
                <td style={{ ...S.tableCellCenter, fontWeight: 700, fontSize: 14, color: S.colors.textPrimary }}>
                  {getWeekTotal() > 0 ? `${getWeekTotal().toFixed(getWeekTotal() % 1 ? 1 : 0)}h` : '–'}
                </td>
                <td />
              </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* ─── History section ─── */}
      <div className="tr-history-section">
        <div className="tr-section-heading">
          <div className="tr-section-title">Mina tidrapporter</div>
          <div className="tr-section-sub">Historik per vecka</div>
        </div>
        <MyPeriodsBoard
          viewMode={historyView}
          onViewModeChange={setHistoryView}
          refreshKey={historyRefreshKey}
          onRefresh={() => setHistoryRefreshKey(k => k + 1)}
        />
      </div>

      {/* ─── Submit confirmation modal ─── */}
      <Modal show={showSubmitConfirm} onHide={() => setShowSubmitConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Bekräfta inlämning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 14, marginBottom: 12 }}>
            <strong>{format(weekStart, 'd MMM')} – {format(weekEnd, 'd MMM yyyy')}</strong>
          </p>
          {(() => {
            const totalMins = weekEntries.reduce((s, e) => s + (e.duration_minutes || 0), 0);
            const totalH = Math.round((totalMins / 60) * 10) / 10;
            const byProject = {};
            weekEntries.forEach(e => {
              const name = e.project_name || e.leave_type_name || 'Övrigt';
              if (!byProject[name]) byProject[name] = { hours: 0, color: e.project_color };
              byProject[name].hours += (e.duration_minutes || 0) / 60;
            });
            return (
              <>
                <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, color: totalH < TARGET_HOURS ? S.colors.warning : S.colors.textPrimary }}>
                  {totalH}h {totalH < TARGET_HOURS && <span style={{ fontSize: 13, fontWeight: 400, color: S.colors.warning }}>({TARGET_HOURS - totalH}h under mål)</span>}
                </div>
                {Object.entries(byProject).map(([name, data]) => (
                  <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={S.colorDot(data.color, 8)} /> {name}
                    </span>
                    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{Math.round(data.hours * 10) / 10}h</span>
                  </div>
                ))}
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowSubmitConfirm(false)}>Granska igen</Button>
          <Button variant="primary" onClick={handleSubmitConfirm} disabled={saving}>
            <Send size={14} style={{ marginRight: 6 }} /> Skicka för godkännande
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── Copy preview modal ─── */}
      <Modal show={showCopyPreview} onHide={() => { setShowCopyPreview(false); setCopyPreviewEntries([]); }} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Kopiera från förra veckan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            const totalMins = copyPreviewEntries.reduce((s, e) => s + (e.duration_minutes || 0), 0);
            const totalH = Math.round((totalMins / 60) * 10) / 10;
            const weekdaysSwe = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
            const byGroup = {};
            copyPreviewEntries.forEach(e => {
              let name; let color;
              if (e.kind === 'absence') {
                const lt = leaveTypes.find(t => t.leave_type_id === e.leave_type_id);
                name = lt?.name || 'Frånvaro';
                color = lt?.color || S.colors.textMuted;
              } else {
                name = e.project_name || 'Övrigt';
                color = e.project_color;
              }
              if (!byGroup[name]) byGroup[name] = { hours: 0, color, days: {} };
              byGroup[name].hours += (e.duration_minutes || 0) / 60;
              const dayName = weekdaysSwe[new Date(e.entry_date).getDay()];
              byGroup[name].days[dayName] = (byGroup[name].days[dayName] || 0) + (e.duration_minutes || 0) / 60;
            });
            return (
              <>
                <p style={{ fontSize: 13, color: S.colors.textMuted, marginBottom: 12 }}>
                  {copyPreviewEntries.length} poster, totalt <strong>{totalH}h</strong>
                </p>
                {Object.entries(byGroup).map(([name, data]) => (
                  <div key={name} style={{ marginBottom: 10, padding: '8px 12px', background: S.colors.bgSubtle, borderRadius: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13, fontWeight: 600 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={S.colorDot(data.color, 8)} /> {name}
                      </span>
                      <span>{Math.round(data.hours * 10) / 10}h</span>
                    </div>
                    <div style={{ fontSize: 11, color: S.colors.textMuted }}>
                      {Object.entries(data.days).map(([day, h]) => `${day} ${Math.round(h * 10) / 10}h`).join(', ')}
                    </div>
                  </div>
                ))}
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => { setShowCopyPreview(false); setCopyPreviewEntries([]); }}>Avbryt</Button>
          <Button variant="primary" onClick={handleCopyConfirm} disabled={saving}>
            <Copy size={14} style={{ marginRight: 6 }} /> Kopiera {copyPreviewEntries.length} poster
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── Quick-create project modal (triggered from add-row dropdown) ─── */}
      <ProjectModal
        show={createProjectName !== null}
        onHide={() => setCreateProjectName(null)}
        onSave={handleQuickCreateProject}
        companyId={companyId}
        defaultName={createProjectName || ''}
      />

      {/* ─── Absence modal ─── */}
      <Modal show={!!absenceModal} onHide={closeAbsenceModal} centered>
        {absenceModal && (
          <>
            <Modal.Header closeButton>
              <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: 10, color: absenceModal.color }}>
                <absenceModal.Icon size={20} />
                Registrera {absenceModal.label.toLowerCase()}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label className="tr-modal-label">Från</label>
                  <input type="date" value={absFrom} onChange={e => setAbsFrom(e.target.value)} className="tr-modal-input" />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="tr-modal-label">Till</label>
                  <input type="date" value={absTo} onChange={e => setAbsTo(e.target.value)} min={absFrom} className="tr-modal-input" />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={absHalfDay} onChange={e => setAbsHalfDay(e.target.checked)} />
                Halv dag (4 timmar per dag)
              </label>
              {absenceModal.code === 'VAB' && (
                <div style={{ marginBottom: 14 }}>
                  <label className="tr-modal-label">Namn på barnet <span style={{ color: S.colors.danger }}>*</span></label>
                  <input type="text" value={absChild} onChange={e => setAbsChild(e.target.value)} placeholder="Ex: Astrid" className="tr-modal-input" />
                  <div style={{ fontSize: 11, color: S.colors.textMuted, marginTop: 4 }}>
                    Försäkringskassan kräver att barnets namn registreras för VAB.
                  </div>
                </div>
              )}
              <div>
                <label className="tr-modal-label">Kommentar (valfritt)</label>
                <input type="text" value={absNote} onChange={e => setAbsNote(e.target.value)} className="tr-modal-input" />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button onClick={closeAbsenceModal} disabled={saving} style={S.btnSecondary}>Avbryt</button>
              <button
                onClick={confirmAbsence}
                disabled={saving || !absFrom || !absTo}
                style={{ ...S.btnPrimary, background: absenceModal.color, borderColor: absenceModal.color }}
              >
                Bekräfta
              </button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  );
};

// Scoped CSS — `.tr-*` prefix, consistent with other modernized pages.
const timesheetCss = `
  /* Darker grid lines so the timesheet reads as a real grid */
  .tr-grid-wrapper tbody tr {
    border-bottom: 1px solid ${S.colors.borderDashed};
  }
  .tr-grid-wrapper tbody tr:last-child {
    border-bottom: none;
  }
  /* No vertical column separators in the grid — horizontal row dividers
     are enough to scan by, and the column lines added visual noise. The
     colored 3px left border on the first cell of each row stays (acts as
     a category indicator, not a column separator). */

  /* Focus-visible + mobile-first grid scaling (from pre-merge page) */
  .tr-grid-wrapper button:focus-visible,
  .tr-grid-wrapper input:focus-visible {
    outline: 2px solid var(--primary, #3b82f6);
    outline-offset: 2px;
  }
  .tr-grid-wrapper {
    position: relative;
    background: linear-gradient(90deg, white 30%, rgba(255,255,255,0)),
                linear-gradient(90deg, rgba(255,255,255,0), white 70%) 100% 0,
                radial-gradient(farthest-side at 0 50%, rgba(0,0,0,0.10), rgba(0,0,0,0)),
                radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,0.10), rgba(0,0,0,0)) 100% 0;
    background-repeat: no-repeat;
    background-color: white;
    background-size: 40px 100%, 40px 100%, 14px 100%, 14px 100%;
    background-attachment: local, local, scroll, scroll;
  }
  @media (max-width: 768px) {
    .tr-grid-wrapper table { min-width: 540px !important; }
    .tr-grid-wrapper thead th { padding: 6px 4px !important; }
    .tr-grid-wrapper tbody td { padding: 4px 2px !important; }
    .tr-grid-wrapper tbody td button { min-height: 44px !important; min-width: 44px !important; font-size: 14px !important; }
    .tr-grid-wrapper tbody td input { min-height: 44px !important; font-size: 16px !important; }
    .tr-grid-wrapper thead th:first-child,
    .tr-grid-wrapper tbody td:first-child { min-width: 140px !important; width: 140px !important; }
  }

  /* Page toolbar */
  .tr-toolbar {
    display: flex; align-items: center; gap: 12px; margin-bottom: 12px;
    padding: 10px 14px; background: white;
    border: 1px solid #e5e7eb; border-radius: 10px;
    flex-wrap: wrap;
  }
  .tr-toolbar-left { display: flex; align-items: center; gap: 6px; }
  .tr-toolbar-pills { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .tr-toolbar-right { display: flex; align-items: center; gap: 8px; margin-left: auto; flex-wrap: wrap; }
  .tr-week-label {
    display: inline-flex; flex-direction: column;
    margin-left: 6px; line-height: 1.2;
  }
  .tr-week-num {
    font-size: 11px; font-weight: 600; color: #6b7280;
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .tr-week-range {
    font-size: 12px; color: #374151; font-variant-numeric: tabular-nums;
  }

  /* Pills in the toolbar (status + balance) */
  .tr-pill {
    font-size: 11px; font-weight: 600;
    padding: 3px 10px; border-radius: 99;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .tr-pill-comp-pos { background: rgba(124,58,237,0.12); color: #7c3aed; }
  .tr-pill-comp-neg { background: rgba(239,68,68,0.12); color: #dc2626; }
  .tr-pill-vac { background: rgba(34,197,94,0.12); color: #15803d; }
  .tr-pill-vac-advance { background: rgba(245,158,11,0.16); color: #b45309; }

  /* Rejection banner */
  .tr-reject-banner {
    display: flex; gap: 12px; align-items: flex-start;
    padding: 14px 18px; margin-bottom: 12px;
    background: rgba(239,68,68,0.06);
    border: 1px solid rgba(239,68,68,0.25);
    border-radius: 10px;
  }

  /* Add-row — sits in the table, just above the totals row */
  .tr-addrow-inner {
    display: flex; justify-content: flex-start;
  }
  .tr-addrow-inner.tr-addrow-centered {
    justify-content: center;
  }
  .tr-addrow-picker { width: 100%; max-width: 360px; }
  .tr-addrow-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 14px; border-radius: 8px;
    border: 1px dashed ${S.colors.borderDashed};
    background: white; color: ${S.colors.textMuted};
    font-size: 13px; font-weight: 500; cursor: pointer;
    transition: all 120ms ease;
  }
  .tr-addrow-btn:hover {
    border-color: var(--primary); color: var(--primary);
    background: var(--rgba-primary-1);
  }

  /* Quick-action strip — timer on the left, absence buttons on the right.
     No cards, no per-section labels — one row on desktop, wraps on mobile. */
  .tr-quick-strip {
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 12px; flex-wrap: wrap;
  }
  .tr-quick-strip-timer {
    flex: 1 1 320px; min-width: 260px;
  }
  .tr-quick-strip-abs {
    display: flex; flex-wrap: wrap; gap: 6px;
  }

  /* Absence quick-add buttons */
  .tr-abs-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 12px; border-radius: 8px;
    border: 1px solid color-mix(in srgb, var(--abs-color) 25%, transparent);
    background: color-mix(in srgb, var(--abs-color) 8%, transparent);
    color: var(--abs-color);
    font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 120ms ease;
  }
  .tr-abs-btn:hover:not(:disabled) {
    background: color-mix(in srgb, var(--abs-color) 14%, transparent);
    border-color: color-mix(in srgb, var(--abs-color) 45%, transparent);
  }
  .tr-abs-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Compact category code cell in the timesheet's TYP column */
  .tr-type-pill {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 40px; height: 22px;
    padding: 0 8px; border-radius: 4px;
    border: 1px solid transparent;
    font-size: 10px; font-weight: 700; letter-spacing: 0.5px;
    background: transparent; color: #6b7280;
    transition: background 120ms ease, border-color 120ms ease;
  }
  .tr-type-pill:hover:not(:disabled) { filter: brightness(0.96); }
  .tr-type-pill:disabled { cursor: default; opacity: 0.7; }
  .tr-type-pill-leave {
    background: rgba(245,158,11,0.12); color: #b45309; border-color: rgba(245,158,11,0.25);
    cursor: default;
  }

  /* Grid view toggle — sits above the week grid */
  .tr-grid-viewbar {
    display: flex; justify-content: flex-end;
    gap: 8px; margin-bottom: 8px; flex-wrap: wrap;
  }
  .tr-view-switch {
    display: inline-flex; gap: 2px;
    background: #f3f4f6; border-radius: 8px; padding: 3px;
  }
  .tr-view-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 12px; border-radius: 6px; border: none;
    background: transparent; color: #6b7280;
    font-size: 12px; font-weight: 500; cursor: pointer;
    transition: all 120ms ease;
  }
  .tr-view-btn:hover { color: #111827; }
  .tr-view-btn.active {
    background: white; color: #111827;
    box-shadow: 0 1px 2px rgba(0,0,0,0.06);
    font-weight: 600;
  }

  /* History section — title block, then MyPeriodsBoard owns its own toolbar */
  .tr-history-section { margin-top: 20px; }
  .tr-section-heading { margin-bottom: 10px; }
  .tr-section-title { font-size: 14px; font-weight: 700; color: #111827; }
  .tr-section-sub { font-size: 11px; color: #6b7280; margin-top: 2px; }

  /* Absence modal inputs */
  .tr-modal-label {
    display: block; margin-bottom: 4px;
    font-size: 11px; font-weight: 600; color: #6b7280;
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .tr-modal-input {
    width: 100%; padding: 8px 10px; font-size: 13px;
    border: 1px solid #e5e7eb; border-radius: 6px; outline: none;
    transition: border-color 150ms, box-shadow 150ms;
  }
  .tr-modal-input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(68,129,78,0.12); }
`;

export default TimesheetPage;
